/**
 * Kitcharo — progressive enhancement.
 *
 * Every feature here sits on top of markup that already works. With this file
 * blocked or broken: the header drawer, submenus, search panel and product
 * accordions still open (native <details>); collection filtering and sorting
 * still submit (a plain GET form with a visible Apply button); the variant
 * picker still resolves a real variant (a <select name="id"> revealed by a
 * <noscript> style); and quantity is still adjustable (native spinner).
 *
 *   1. Disclosure panels — Escape, click-outside, focus handling
 *   2. Collection filters — hide Apply, auto-submit on change
 *   3. Variant picker — update id, price, availability, SKU, gallery
 *   4. Quantity stepper, and cart quantity submission
 *   5. Media gallery — sync the active thumbnail while scrolling
 *   6. Product recommendations — fetch real related products
 *
 * No dependencies, no framework, nothing that blocks rendering.
 */
(function () {
  'use strict';

  /* ===============================================================
     1. Disclosure panels
     =============================================================== */
  var PANEL_SELECTOR = 'details[data-drawer], details[data-header-search]';

  /** Returns every currently open drawer or search panel. */
  function openPanels() {
    return Array.prototype.slice.call(
      document.querySelectorAll(PANEL_SELECTOR)
    ).filter(function (panel) {
      return panel.open;
    });
  }

  /**
   * Closes a <details> panel and hands focus back to its <summary>, so
   * keyboard users are not dropped at the top of the document.
   */
  function close(panel, restoreFocus) {
    if (!panel || !panel.open) return;

    panel.open = false;

    if (restoreFocus) {
      var summary = panel.querySelector('summary');
      if (summary) summary.focus();
    }
  }

  function closeAll(restoreFocus) {
    openPanels().forEach(function (panel) {
      close(panel, restoreFocus);
    });
  }

  /* Escape closes whatever is open. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (openPanels().length === 0) return;

    closeAll(true);
  });

  /* Pointer interactions outside a panel's content close it. */
  document.addEventListener('click', function (event) {
    var target = event.target;

    /* Guards against non-element targets, which have no `closest`. */
    if (!target || typeof target.closest !== 'function') return;

    /* The drawer's explicit close button and its backdrop. */
    var closer = target.closest('[data-drawer-close], [data-drawer-overlay]');
    if (closer) {
      close(closer.closest('details'), true);
      return;
    }

    /* Anything outside an open panel dismisses it, except its own summary
       (which toggles natively) and the panel's own contents. */
    openPanels().forEach(function (panel) {
      if (!panel.contains(target)) close(panel, false);
    });
  });

  /* Move focus into a panel as it opens. */
  document.addEventListener(
    'toggle',
    function (event) {
      var panel = event.target;
      if (!panel.matches || !panel.matches(PANEL_SELECTOR)) return;
      if (!panel.open) return;

      /* Only one panel may be open at a time. */
      openPanels().forEach(function (other) {
        if (other !== panel && !other.contains(panel)) close(other, false);
      });

      var focusTarget =
        panel.querySelector('input[type="search"]') ||
        panel.querySelector('[data-drawer-close]');

      if (!focusTarget) return;

      /* Wait a frame so the panel is laid out and focusable. */
      requestAnimationFrame(function () {
        focusTarget.focus({ preventScroll: true });
      });
    },
    /* `toggle` does not bubble, so listen during the capture phase. */
    true
  );

  /* ===============================================================
     2. Collection filters
     =============================================================== */
  (function initFilters() {
    var form = document.querySelector('[data-filter-form]');
    if (!form) return;

    var submit = form.querySelector('[data-filter-submit]');
    var hasPriceRange = form.querySelector('input[type="number"]') !== null;

    /* Checkboxes and the sort select can submit on their own, so the Apply
       button is redundant for them. A price range cannot: `change` fires when
       the shopper leaves the "from" field, which would reload the page before
       they ever reach "to". So when a price filter is present Apply stays,
       and number inputs are left to it. */
    if (submit && !hasPriceRange) submit.hidden = true;

    form.addEventListener('change', function (event) {
      var field = event.target;
      if (!field.name) return;
      if (field.type === 'number') return;

      form.submit();
    });
  })();

  /* ===============================================================
     3. Variant picker
     =============================================================== */
  (function initVariantPicker() {
    var picker = document.querySelector('[data-variant-picker]');
    if (!picker) return;

    var dataScript = picker.querySelector('[data-variant-data]');
    if (!dataScript) return;

    var variants;
    try {
      variants = JSON.parse(dataScript.textContent);
    } catch (error) {
      /* Malformed data must not break the page — the <select> still works. */
      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    var form = picker.closest('form');
    var idField = document.querySelector('[data-variant-id]');
    var priceTarget = document.querySelector('[data-price]');
    var addButton = document.querySelector('[data-add-to-cart]');
    var addButtonText = document.querySelector('[data-add-to-cart-text]');
    var skuTarget = document.querySelector('[data-variant-sku]');
    var soldOutLabel = addButtonText ? addButtonText.dataset.soldOut : null;

    /** Reads the currently checked radio for each option position. */
    function selectedOptions() {
      var checked = picker.querySelectorAll('.variant-picker__input:checked');
      var options = [];

      Array.prototype.forEach.call(checked, function (input) {
        var position = parseInt(input.dataset.optionPosition, 10);
        options[position - 1] = input.value;
      });

      return options;
    }

    function findVariant(options) {
      return variants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return value === options[index];
        });
      });
    }

    /* Marks option values that no combination offers, so the shopper can see
       what exists instead of silently failing on submit. */
    function markUnavailable(options) {
      var inputs = picker.querySelectorAll('.variant-picker__input');

      Array.prototype.forEach.call(inputs, function (input) {
        var position = parseInt(input.dataset.optionPosition, 10);
        var candidate = options.slice();
        candidate[position - 1] = input.value;

        var match = variants.find(function (variant) {
          return variant.options.every(function (value, index) {
            /* Only compare the positions decided so far. */
            return candidate[index] === undefined || value === candidate[index];
          });
        });

        input.dataset.unavailable = match && match.available ? 'false' : 'true';
      });
    }

    function updateLabels(options) {
      options.forEach(function (value, index) {
        var label = picker.querySelector(
          '[data-option-selected="' + (index + 1) + '"]'
        );
        if (label) label.textContent = value;
      });
    }

    function updatePrice(variant) {
      if (!priceTarget) return;

      var amount = priceTarget.querySelector('[data-price-amount]');
      var compare = priceTarget.querySelector('[data-price-compare]');
      var compareWrapper = priceTarget.querySelector(
        '[data-price-compare-wrapper]'
      );
      var unit = priceTarget.querySelector('[data-price-unit]');
      var unitWrapper = priceTarget.querySelector('[data-price-unit-wrapper]');

      /* Amounts are pre-formatted by Shopify's `money` filter, so no currency
         formatting happens here. They can carry HTML entities depending on the
         store's currency format, hence innerHTML rather than textContent —
         the values are store data, never shopper input. Only these inner
         spans are written, leaving the visually hidden labels intact. */
      if (amount) amount.innerHTML = variant.price;

      if (compare && compareWrapper) {
        compare.innerHTML = variant.compare_at_price || '';
        compareWrapper.hidden = !variant.compare_at_price;
      }

      if (unit && unitWrapper) {
        unit.innerHTML = variant.unit_price || '';
        unitWrapper.hidden = !variant.unit_price;
      }

      priceTarget.classList.toggle(
        'price--on-sale',
        Boolean(variant.compare_at_price)
      );
    }

    function updateAvailability(variant) {
      if (!addButton) return;

      addButton.disabled = !variant.available;

      if (addButtonText) {
        addButtonText.textContent = variant.available
          ? addButtonText.dataset.addLabel || addButtonText.textContent
          : soldOutLabel || addButtonText.textContent;
      }
    }

    /* Scrolls the gallery to the image this variant is assigned to. */
    function updateGallery(variant) {
      if (!variant.media_id) return;

      var item = document.getElementById('ProductMedia-' + variant.media_id);
      if (!item) return;

      var list = item.closest('[data-media-list]');
      /* Only scroll the horizontal mobile carousel. On desktop the column is
         fully visible and moving the page under the shopper would be rude. */
      if (list && list.scrollWidth > list.clientWidth) {
        var offset =
          item.getBoundingClientRect().left -
          list.getBoundingClientRect().left +
          list.scrollLeft;
        list.scrollTo({ left: offset, behavior: 'smooth' });
      }

      setActiveThumb(variant.media_id);
    }

    function update() {
      var options = selectedOptions();
      var variant = findVariant(options);

      markUnavailable(options);
      updateLabels(options);

      if (!variant) {
        /* An impossible combination: block the sale rather than guess. */
        if (addButton) addButton.disabled = true;
        return;
      }

      if (idField) idField.value = variant.id;
      updatePrice(variant);
      updateAvailability(variant);
      if (skuTarget) skuTarget.textContent = variant.sku || '';
      updateGallery(variant);

      /* Keeps the URL shareable and the back button meaningful. */
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    picker.addEventListener('change', function (event) {
      if (!event.target.classList.contains('variant-picker__input')) return;
      update();
    });

    /* Reflect the initial state, including unavailable combinations. */
    markUnavailable(selectedOptions());

    /* Guards against a stray second `id` control sneaking into the form. */
    if (form && form.querySelectorAll('[name="id"]').length > 1) {
      console.warn('Kitcharo: more than one "id" field in the product form.');
    }
  })();

  /* ===============================================================
     4. Quantity stepper
     =============================================================== */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest('[data-quantity-step]');
    if (!button) return;

    var wrapper = button.closest('[data-quantity]');
    var input = wrapper && wrapper.querySelector('.quantity__input');
    if (!input) return;

    var step = parseInt(button.dataset.quantityStep, 10) || 0;
    var min = parseInt(input.min, 10) || 1;
    var next = (parseInt(input.value, 10) || min) + step;

    input.value = next < min ? min : next;
    /* Dispatched so anything listening for quantity changes still hears it. */
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ===============================================================
     4b. Cart quantity — submit on change
     =============================================================== */
  (function initCart() {
    var form = document.querySelector('[data-cart-form]');
    if (!form) return;

    /* The Update button is the no-JS path for committing a quantity. */
    var update = form.querySelector('[data-cart-update]');
    if (update) update.hidden = true;

    form.addEventListener('change', function (event) {
      if (!event.target.classList.contains('quantity__input')) return;

      /* `requestSubmit` runs validation and fires the submit event, unlike
         `submit()`. Named so the cart updates rather than checking out. */
      if (update && form.requestSubmit) {
        form.requestSubmit(update);
      } else {
        form.submit();
      }
    });
  })();

  /* ===============================================================
     5. Media gallery
     =============================================================== */
  function setActiveThumb(mediaId) {
    var thumbs = document.querySelectorAll('[data-media-thumb]');
    if (thumbs.length === 0) return;

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.classList.toggle(
        'product-media__thumb--active',
        thumb.dataset.mediaThumb === String(mediaId)
      );
    });
  }

  (function initGallery() {
    var list = document.querySelector('[data-media-list]');
    if (!list) return;
    if (!('IntersectionObserver' in window)) return;

    /* Keeps the thumbnail indicator in step with the scroll position. */
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            setActiveThumb(entry.target.dataset.mediaId);
          }
        });
      },
      { root: list, threshold: 0.6 }
    );

    Array.prototype.forEach.call(
      list.querySelectorAll('[data-media-id]'),
      function (item) {
        observer.observe(item);
      }
    );
  })();

  /* ===============================================================
     6. Product recommendations
     =============================================================== */
  (function initRecommendations() {
    var container = document.querySelector('[data-related-products]');
    if (!container || !container.dataset.url) return;
    if (!('fetch' in window)) return;

    /* Only fetch once the shopper is near the section. */
    function load() {
      fetch(container.dataset.url)
        .then(function (response) {
          if (!response.ok) throw new Error('Request failed');
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-related-products]');

          /* Replaces the shell only when real recommendations came back, so
             an empty result leaves no stray heading behind. */
          if (fresh && fresh.innerHTML.trim()) {
            container.innerHTML = fresh.innerHTML;
          }
        })
        .catch(function () {
          /* A failed request simply leaves the section empty. */
        });
    }

    if (!('IntersectionObserver' in window)) {
      load();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, self) {
        if (entries[0].isIntersecting) {
          self.disconnect();
          load();
        }
      },
      { rootMargin: '400px' }
    );

    observer.observe(container);
  })();
})();
